import { flatMap } from 'lodash';
import { REPORT_FIELD_SERVICE_HOSTS } from './config';
import {
  getGetRequestInit,
  getProxyServicePath,
  retrieveData,
} from './common';

export const getAvailableReportFields = async ({
  name,
  group,
  type,
}, token) => {
  const serviceEndpoint = getProxyServicePath(REPORT_FIELD_SERVICE_HOSTS);
  const url = `${serviceEndpoint}?service=dashboard${getQueryString({ name, group, type }, token)}`;

  const requestInit = getGetRequestInit(token);
  const resp = await retrieveData(url, requestInit);

  return resp.reportFields;
};

const getQueryString = (params) => {
  const queryString = Object.keys(params)
    .filter(p => params[p] !== undefined)
    .map(p => `${p}=${params[p]}`)
    .join('&');

  return queryString ? `&${queryString}` : '';
};

export const getEntitiesExceptOnlyGroup = (entities, group) => {
  return entities.filter(e => !onlyContains(e.groups, group));
};

export const getUniqueGroups = (entities) => {
  return getUniqueStrings(flatMap(entities, e => e.groups));
};

export const getReportFieldFromValue = (allReportFields, value) => {
  return getEntityFromValue(allReportFields, value);
};

export const getReportFieldFromSnake = (allReportFields, snake) => {
  return getEntityFromSnake(allReportFields, snake);
};

export const getReportFieldKeyFromSnake = (allReportFields, snake) => {
  return getEntityKeyFromSnake(allReportFields, snake);
};

export const getReportFieldSnakeFromKey = (allReportFields, key) => {
  return getEntitySnakeFromKey(allReportFields, key);
};

export const getReportFieldSnakeFromValue = (allReportFields, value) => {
  return getEntitySnakeFromValue(allReportFields, value);
};

export const getReportFieldsFromValues = (allReportFields, values) => {
  return getEntitiesFromValues(allReportFields, values);
};

export const getDemographicsFieldName = (reportField) => {
  return reportField.snake === 'page_views'
    ? 'Measurable Views'
    : reportField.key;
};

export const getDemographicsFieldSnake = (reportField) => {
  return reportField.snake === 'page_views'
    ? 'measurable_views'
    : reportField.snake;
};

export const getFieldSuffix = (reportField) => {
  return reportField.key[0] === '%'
    ? reportField.key.slice(1).toLowerCase()
    : reportField.key.toLowerCase();
};

const getEntityKeyFromSnake = (entities, snake) => {
  const entity = getEntityFromSnake(entities, snake);

  return getEntityKey(entity, snake);
};

const getEntitySnakeFromKey = (entities, key) => {
  const entity = getEntityFromKey(entities, key);

  return getEntitySnake(entity, key);
};

const getEntitySnakeFromValue = (entities, value) => {
  const entity = getEntityFromValue(entities, value);

  return getEntitySnake(entity, value);
};

const getEntityKey = (entity, defaultVal) => {
  return (entity && entity.key) || defaultVal;
};

const getEntitySnake = (entity, defaultVal) => {
  return (entity && entity.snake) || defaultVal;
};

const getEntityFromKey = (entities, key) => {
  return getEntityFromProp(entities, 'key', key);
};

const getEntityFromValue = (entities, value) => {
  return getEntityFromProp(entities, 'value', value);
};

const getEntityFromSnake = (entities, snake) => {
  return getEntityFromProp(entities, 'snake', snake);
};

const getEntityFromProp = (entities, prop, val) => {
  const predicate = getEntityPredicate(prop);

  return entities.find(e => predicate(e, val));
};

const getEntitiesFromValues = (entities, values) => {
  return getEntitiesFromProp(entities, 'value', values);
};

const getEntitiesFromProp = (entities, prop, vals) => {
  const predicate = getEntitiesPredicate(prop);

  return entities.filter(e => predicate(e, vals));
};

const getEntityPredicate = (prop) => {
  switch (prop) {
    case 'key':
      return keyPredicate;
    case 'value':
      return valuePredicate;
    case 'snake':
      return snakePredicate;
    default:
      throw `Invalid predicate prop: ${prop}`;
  }
};

const getEntitiesPredicate = (prop) => {
  switch (prop) {
    case 'key':
      return keysPredicate;
    case 'value':
      return valuesPredicate;
    case 'snake':
      return snakesPredicate;
    default:
      throw `Invalid predicate prop: ${prop}`;
  }
};

const keyPredicate = (e, key) => e.key === key;
const valuePredicate = (e, value) => e.value === value;
const snakePredicate = (e, snake) => e.snake === snake;
const keysPredicate = (e, keys) => contains(keys, e.key);
const valuesPredicate = (e, values) => contains(values, e.value);
const snakesPredicate = (e, snakes) => contains(snakes, e.snake);

const onlyContains = (arr, str) => arr.length === 1 && arr[0] === str;
const contains = (arr, str) => arr.indexOf(str) >= 0;

const getUniqueStrings = (arr) => {
  return arr.reduce((uniqueStrs, str, index, collection) => {
    if (collection.indexOf(str) === index) {
      uniqueStrs.push(str);
    }

    return uniqueStrs;
  }, []);
};
